import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Pagination from '../../common/Pagination';
import BookingPatientCard from './BookingPatientCard';
import WebBookingsSkeleton from './subparts/WebBookingsSkeleton';
import bookingService from '../../../services/booking.service';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import {
    buildBookingRequestParams,
    DEFAULT_WEB_BOOKING_SEARCH,
    WEB_BOOKING_SEARCH_COLUMNS,
} from './subparts/webBookingsQuery';

const PAGE_SIZE = 10;

function BookingRequests({ bookingStatus, tabId, tabLabel, refreshToken, onCountUpdate }) {
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);

    const [loading, setLoading] = useState(true);
    const [patientRequests, setPatientRequests] = useState([]);
    const [pageNo, setPageNo] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const [filters, setFilters] = useState(DEFAULT_WEB_BOOKING_SEARCH);
    const [appliedFilters, setAppliedFilters] = useState(DEFAULT_WEB_BOOKING_SEARCH);
    const onCountUpdateRef = useRef(onCountUpdate);

    useEffect(() => {
        onCountUpdateRef.current = onCountUpdate;
    }, [onCountUpdate]);

    const fetchBookingRequests = useCallback(
        async (page, search) => {
            if (!clinicId) {
                return;
            }

            setLoading(true);

            try {
                const params = buildBookingRequestParams(bookingStatus, search);
                const res = await bookingService.getBookingRequests(clinicId, page, PAGE_SIZE, params);
                const data = res.data || {};

                setPatientRequests(data.content || []);
                setTotalPages(data.totalPages || 0);
                setTotalElements(data.totalElements || 0);

                if (tabId === 'new') {
                    onCountUpdateRef.current?.('new', data.totalElements || 0);
                }
            } catch (error) {
                console.error('Error fetching booking requests:', error);
                toast.error('Failed to load booking requests');
            } finally {
                setLoading(false);
            }
        },
        [bookingStatus, clinicId, tabId]
    );

    useEffect(() => {
        fetchBookingRequests(pageNo, appliedFilters);
    }, [fetchBookingRequests, pageNo, appliedFilters, refreshToken]);

    const handleSearch = (event) => {
        event.preventDefault();
        setPageNo(0);
        setAppliedFilters(filters);
    };

    const handleClearSearch = () => {
        setFilters(DEFAULT_WEB_BOOKING_SEARCH);
        setAppliedFilters(DEFAULT_WEB_BOOKING_SEARCH);
        setPageNo(0);
    };

    const handlePageChange = (newPage) => {
        setPageNo(newPage);
    };

    const handleUpdate = () => {
        fetchBookingRequests(pageNo, appliedFilters);
    };

    return (
        <>
            <div className="web-bookings-filters">
                <form className="web-bookings-filters__row" onSubmit={handleSearch}>
                    <select
                        className="form-select form-select-sm web-bookings-filters__column"
                        value={filters.column}
                        onChange={(event) => setFilters((prev) => ({ ...prev, column: event.target.value }))}
                        disabled={loading}
                    >
                        {WEB_BOOKING_SEARCH_COLUMNS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>

                    <div className="web-bookings-filters__search input-group input-group-sm">
                        <span className="input-group-text bg-white border-end-0">
                            <i className="bi bi-search text-muted"></i>
                        </span>
                        <input
                            type="text"
                            className="form-control form-control-sm border-start-0"
                            placeholder="Search booking requests..."
                            value={filters.value}
                            onChange={(event) => setFilters((prev) => ({ ...prev, value: event.target.value }))}
                            disabled={loading}
                        />
                        <button type="submit" className="btn btn-sm btn-accent" disabled={loading}>
                            Search
                        </button>
                    </div>

                    {appliedFilters.value && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={handleClearSearch}
                            disabled={loading}
                        >
                            Clear
                        </button>
                    )}
                </form>
            </div>

            <div className="web-bookings-toolbar">
                <h2 className="web-bookings-toolbar__title">{tabLabel}</h2>
                <div className="web-bookings-toolbar__meta">
                    <span className="ecx-page-chip">
                        <i className="bi bi-inbox"></i>
                        {totalElements} request{totalElements === 1 ? '' : 's'}
                    </span>
                    {totalPages > 0 && (
                        <span className="ecx-page-chip">
                            Page {pageNo + 1} of {Math.max(totalPages, 1)}
                        </span>
                    )}
                </div>
            </div>

            <div className="web-bookings-list ecx-page-scroll">
                {loading ? (
                    <WebBookingsSkeleton count={3} />
                ) : patientRequests.length > 0 ? (
                    <div className="web-bookings-list__items">
                        {patientRequests.map((patient) => (
                            <BookingPatientCard
                                key={patient.id}
                                request={patient}
                                handleUpdate={handleUpdate}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="ecx-page-empty">
                        <span className="ecx-page-empty__icon">
                            <i className="bi bi-calendar-check"></i>
                        </span>
                        <h3 className="h6 fw-bold text-dark mb-1">No requests found</h3>
                        <p className="mb-0 small">
                            {appliedFilters.value
                                ? 'Try adjusting your search filters.'
                                : 'Booking requests for this section will appear here.'}
                        </p>
                    </div>
                )}
            </div>

            {!loading && totalPages > 1 && (
                <div className="ecx-page-pagination border-top p-3">
                    <Pagination
                        currentPage={pageNo}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        totalElements={totalElements}
                        pageSize={PAGE_SIZE}
                    />
                </div>
            )}
        </>
    );
}

export default BookingRequests;
